import ELK from 'elkjs';

import { TpETLPipeline } from '../interfaces';
import { getETLComponentWithOutputObj } from '../utils';

const componentNodeSizeX = 150;
const componentNodeSizeY = 100;
const componentPortSize = 20;
const portLabelSizeX = componentPortSize;
const portLabelSizeY = componentPortSize;

function portId(compId: string, outputId: string): string {
  // Generates a unique port ID for the component and output ID
  return `outp_${compId}_${outputId}`;
}

export async function getLayoutForPipeline(pipeline: TpETLPipeline) {
  // Calculates the visual layout of the pipeline as a directed acyclic graph
  const elk = new ELK();
  const children: any[] = [];
  const edges: any[] = [];
  for (const comp of pipeline.components) {
    children.push({
      id: comp.id,
      width: componentNodeSizeX,
      height: componentNodeSizeY,
      properties: {
        portConstraints: 'FIXED_SIDE', // This enables the port side option
        'nodeSize.minimum': `(${componentNodeSizeX},${componentNodeSizeY})`, // for constraint below
        'nodeSize.constraints': '[PORTS MINIMUM_SIZE]', //  node grows in size in case of many ports
        'elk.portLabels.placement': 'OUTSIDE', // Port labels are placed outside the node
      },
      ports:
        Object.values(comp.outputObjectIds).map((outputId) => {
          const outputType = getETLComponentWithOutputObj(pipeline, outputId).outputType;
          return {
            id: portId(comp.id, outputId),
            width: componentPortSize,
            height: componentPortSize,
            labels: [{ text: outputType.name, width: portLabelSizeX, height: portLabelSizeY }],
            properties: {
              side: 'EAST', // EAST is output (right) side
              borderOffset: (-1 * componentPortSize) / 2, // center port over border
              outputId: outputId,
              outputName: outputType.name,
            },
          };
        }) || [],
    });
    for (const inpConnect of Object.values(comp.inputObjectIds)) {
      const sourceComp = getETLComponentWithOutputObj(pipeline, inpConnect);
      edges.push({
        id: `inp_${comp.id}_${inpConnect}`,
        source: sourceComp.comp.id,
        sourcePort: portId(sourceComp.comp.id, sourceComp.comp.outputObjectIds[sourceComp.outputType.id]),
        target: comp.id,
      });
    }
    for (const extraParam of comp.extraParamObjectIds) {
      const sourceComp = getETLComponentWithOutputObj(pipeline, extraParam);
      edges.push({
        id: `extraParam_${comp.id}_${extraParam}`,
        source: sourceComp.comp.id,
        sourcePort: portId(sourceComp.comp.id, sourceComp.comp.outputObjectIds[sourceComp.outputType.id]),
        target: comp.id,
      });
    }
  }

  const graph = {
    id: 'root',
    properties: {
      'elk.algorithm': 'layered',
      'elk.edgeRouting': 'ORTHOGONAL',
      'elk.direction': 'RIGHT',
      'elk.spacing.nodeNode': 50,
      'elk.spacing.componentComponent': 50,
      'elk.layered.spacing.nodeNodeBetweenLayers': 50,
      'elk.spacing.labelPortVertical': 0, // No vertical spacing between port labels and ports
      'elk.spacing.labelPortHorizontal': 0,
    },
    children,
    edges,
  };

  return elk.layout(graph);
}
